import { useState, useEffect } from 'react';
import { Users, TreePine, Heart, Search, BookOpen, Star, MapPin, Crown, Plus, Upload, FileText, Settings, Camera, Link2 } from 'lucide-react';
import FamilyTreeView from '../components/genealogy/FamilyTreeView';
import PersonCard from '../components/genealogy/PersonCard';
import PersonForm from '../components/genealogy/PersonForm';
import MediaManager from '../components/genealogy/MediaManager';
import RelationshipManager from '../components/genealogy/RelationshipManager';
import type { Person, FamilyTreeData } from '../components/genealogy/types';

const Whakapapa = () => {
  useEffect(() => {
    document.title = 'Whakapapa | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [familyData, setFamilyData] = useState<FamilyTreeData>({ people: {}, rootPersonId: undefined });
  
  // Modal states
  const [showPersonForm, setShowPersonForm] = useState(false);
  const [showMediaManager, setShowMediaManager] = useState(false);
  const [showRelationshipManager, setShowRelationshipManager] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);

  const handlePersonSelect = (person: Person) => {
    setSelectedPerson(person);
  };

  const handlePersonEdit = (person: Person) => {
    setEditingPerson(person);
    setShowPersonForm(true);
  };

  const handleAddPerson = () => {
    setEditingPerson(null);
    setShowPersonForm(true);
  };

  const handleSavePerson = (person: Person) => {
    setFamilyData(prev => ({
      ...prev,
      people: {
        ...prev.people,
        [person.id]: person
      }
    }));
    
    // If this is the first person and no root is set, make them the root
    if (Object.keys(familyData.people).length === 0 && !familyData.rootPersonId) {
      setFamilyData(prev => ({
        ...prev,
        rootPersonId: person.id
      }));
    }
    
    setEditingPerson(null);
  };

  const handleMediaUpdate = (personId: string, photos: string[], documents: string[]) => {
    if (!familyData.people[personId]) return;
    
    const updatedPerson = {
      ...familyData.people[personId],
      photos,
      documents
    };
    
    setFamilyData(prev => ({
      ...prev,
      people: {
        ...prev.people,
        [personId]: updatedPerson
      }
    }));
    
    // Update selected person if it's the same
    if (selectedPerson?.id === personId) {
      setSelectedPerson(updatedPerson);
    }
  };

  const handleRelationshipUpdate = (updatedPerson: Person) => {
    setFamilyData(prev => ({
      ...prev,
      people: {
        ...prev.people,
        [updatedPerson.id]: updatedPerson
      }
    }));
    
    // Update selected person if it's the same
    if (selectedPerson?.id === updatedPerson.id) {
      setSelectedPerson(updatedPerson);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-ocean-teal to-ocean-deep text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Whakapapa
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
            Discovering your genealogical connections and strengthening your identity through whakapapa
          </p>
        </div>
      </section>

      {/* Interactive Family Tree Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="section-header">Your Whakapapa Tree</h2>
            <div className="flex space-x-4">
              <button 
                onClick={handleAddPerson}
                className="btn-primary flex items-center"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Family Member
              </button>
              <button className="btn-secondary flex items-center">
                <Upload className="h-5 w-5 mr-2" />
                Import GEDCOM
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Family Tree Visualization */}
            <div className="lg:col-span-2">
              <FamilyTreeView
                data={familyData}
                onPersonSelect={handlePersonSelect}
                onPersonEdit={handlePersonEdit}
                onAddPerson={handleAddPerson}
                selectedPersonId={selectedPerson?.id}
              />
            </div>

            {/* Person Details Panel */}
            <div className="space-y-6">
              {selectedPerson ? (
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Selected Person</h3>
                  <PersonCard
                    person={selectedPerson}
                    onEdit={handlePersonEdit}
                    isSelected={true}
                  />
                    {/* Additional Details */}
                  <div className="mt-6 space-y-4">
                    {/* Enhanced Action Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setShowMediaManager(true)}
                        className="flex items-center justify-center p-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Manage Media
                      </button>
                      <button
                        onClick={() => setShowRelationshipManager(true)}
                        className="flex items-center justify-center p-3 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Relationships
                      </button>
                    </div>

                    {selectedPerson.photos && selectedPerson.photos.length > 0 && (
                      <div className="card">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <Camera className="h-4 w-4 mr-2" />
                          Photos ({selectedPerson.photos.length})
                        </h4><div className="grid grid-cols-2 gap-2">
                          {selectedPerson.photos.slice(0, 4).map((_, index) => (
                            <div key={index} className="bg-gray-200 h-20 rounded flex items-center justify-center text-gray-500 text-sm">
                              Photo {index + 1}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedPerson.stories && selectedPerson.stories.length > 0 && (
                      <div className="card">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <BookOpen className="h-4 w-4 mr-2" />
                          Stories & Memories
                        </h4>
                        <div className="space-y-2">
                          {selectedPerson.stories.slice(0, 3).map((story, index) => (
                            <div key={index} className="p-3 bg-gray-50 rounded text-sm">
                              {story.length > 100 ? `${story.substring(0, 100)}...` : story}
                            </div>
                          ))}
                        </div>
                      </div>                    )}

                    {selectedPerson.documents && selectedPerson.documents.length > 0 && (
                      <div className="card">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <FileText className="h-4 w-4 mr-2" />
                          Documents
                        </h4>
                        <div className="space-y-2">
                          {selectedPerson.documents.slice(0, 3).map((_, index) => (
                            <div key={index} className="flex items-center p-2 bg-gray-50 rounded text-sm">
                              <FileText className="h-4 w-4 mr-2 text-gray-400" />
                              Document {index + 1}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="card text-center">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-semibold mb-2 text-gray-800">Select a Person</h3>
                  <p className="text-gray-600">
                    Click on any person in the tree to view their details, photos, stories, and documents.
                  </p>
                </div>
              )}

              {/* Quick Actions */}
              <div className="card">
                <h4 className="font-semibold mb-3 text-gray-800">Quick Actions</h4>
                <div className="space-y-2">
                  <button 
                    onClick={handleAddPerson}
                    className="w-full text-left p-3 rounded hover:bg-gray-50 flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-3 text-green-600" />
                    Add New Person
                  </button>
                  <button className="w-full text-left p-3 rounded hover:bg-gray-50 flex items-center">
                    <Upload className="h-4 w-4 mr-3 text-blue-600" />
                    Import GEDCOM File
                  </button>
                  <button className="w-full text-left p-3 rounded hover:bg-gray-50 flex items-center">
                    <Settings className="h-4 w-4 mr-3 text-gray-600" />
                    Family Tree Settings
                  </button>
                  <button className="w-full text-left p-3 rounded hover:bg-gray-50 flex items-center">
                    <FileText className="h-4 w-4 mr-3 text-purple-600" />
                    Export Family Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="section-header">Understanding Whakapapa</h2>
            <div className="card bg-ocean-blue bg-opacity-10">
              <p className="text-lg text-gray-700 leading-relaxed">
                Whakapapa is more than genealogy - it's the foundation of Māori identity, connecting you to ancestors, 
                whenua (land), and your place in the universe. Understanding your whakapapa strengthens your cultural 
                identity and provides a pathway to reconnect with iwi, hapū, and whānau.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="card text-center">
              <Users className="h-10 w-10 text-ocean-teal mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Identity</h3>
              <p className="text-gray-600 text-sm">Your whakapapa defines who you are and where you belong</p>
            </div>

            <div className="card text-center">
              <MapPin className="h-10 w-10 text-ocean-teal mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Whenua Connection</h3>
              <p className="text-gray-600 text-sm">Links you to specific lands and territories</p>
            </div>

            <div className="card text-center">
              <Crown className="h-10 w-10 text-ocean-teal mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Rights & Responsibilities</h3>
              <p className="text-gray-600 text-sm">Establishes your rights and obligations within iwi and hapū</p>
            </div>

            <div className="card text-center">
              <Heart className="h-10 w-10 text-ocean-teal mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Spiritual Connection</h3>
              <p className="text-gray-600 text-sm">Connects you to ancestors and spiritual traditions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Researching Your Whakapapa</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="card">
                <div className="flex items-start">
                  <Search className="h-8 w-8 text-ocean-teal mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">1. Start with What You Know</h3>
                    <p className="text-gray-600 mb-3">
                      Begin by documenting what you already know about your whānau. Talk to older family members, 
                      collect birth certificates, marriage records, and any existing family trees.
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Interview kaumātua and older whānau members</li>
                      <li>Collect official documents (birth, death, marriage certificates)</li>
                      <li>Look for family photographs with names and dates</li>
                      <li>Document iwi and hapū affiliations you know</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-start">
                  <BookOpen className="h-8 w-8 text-ocean-teal mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">2. Use Official Records</h3>
                    <p className="text-gray-600 mb-3">
                      New Zealand has extensive records that can help trace your whakapapa. 
                      Many are now available online or through official channels.
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Department of Internal Affairs (BDM records)</li>
                      <li>Archives New Zealand</li>
                      <li>Māori Land Court records</li>
                      <li>Electoral rolls and census records</li>
                      <li>Military service records</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-start">
                  <Users className="h-8 w-8 text-ocean-teal mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">3. Connect with Iwi and Hapū</h3>
                    <p className="text-gray-600 mb-3">
                      Iwi and hapū organizations often maintain their own records and can help verify 
                      connections and provide additional genealogical information.
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Contact iwi registrars or genealogists</li>
                      <li>Attend hui and cultural events</li>
                      <li>Join iwi databases and registers</li>
                      <li>Connect with marae communities</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-start">
                  <TreePine className="h-8 w-8 text-ocean-teal mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">4. DNA and Modern Tools</h3>
                    <p className="text-gray-600 mb-3">
                      Modern DNA testing and online tools can help confirm connections and 
                      identify previously unknown relatives.
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>DNA testing through reputable companies</li>
                      <li>Online genealogy platforms</li>
                      <li>Māori genealogy websites and databases</li>
                      <li>Social media genealogy groups</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Your Results */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Understanding Your Whakapapa</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <Star className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Iwi Affiliation</h3>
              <p className="text-gray-600 mb-4">
                Your iwi connection is typically traced through your ancestors and determines 
                your broader tribal identity and territorial connections.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Large tribal groups with defined territories</li>
                <li>Usually traced through male or female lines</li>
                <li>Determines mana whenua (territorial rights)</li>
                <li>Provides access to iwi services and benefits</li>
              </ul>
            </div>

            <div className="card">
              <MapPin className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Hapū Connection</h3>
              <p className="text-gray-600 mb-4">
                Hapū are sub-tribal groups within iwi, often connected to specific locations 
                and providing more intimate community connections.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Smaller groups within iwi structure</li>
                <li>Often associated with specific marae</li>
                <li>Stronger day-to-day cultural connections</li>
                <li>Direct involvement in local decisions</li>
              </ul>
            </div>

            <div className="card">
              <Users className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Whānau Networks</h3>
              <p className="text-gray-600 mb-4">
                Extended family networks that provide immediate support, cultural guidance, 
                and personal relationships within your iwi and hapū.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Immediate and extended family groups</li>
                <li>Support networks for daily life</li>
                <li>Cultural learning environments</li>
                <li>Child-rearing and elder care</li>
              </ul>
            </div>

            <div className="card">
              <Crown className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Rights and Responsibilities</h3>
              <p className="text-gray-600 mb-4">
                Understanding your whakapapa helps clarify your rights to participate in 
                iwi decisions and your responsibilities to support the collective.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Voting rights in iwi matters</li>
                <li>Access to iwi resources and services</li>
                <li>Responsibilities to contribute to collective wellbeing</li>
                <li>Participation in cultural and ceremonial events</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Hapū Registrar */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Hapū Registrar</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="card bg-ocean-blue bg-opacity-10 text-center">
              <Users className="h-16 w-16 text-ocean-teal mx-auto mb-6" />
              <h3 className="text-2xl font-semibold mb-4 text-ocean-deep">Join Our Hapū Registry</h3>
              <p className="text-gray-700 mb-6">
                Register with our hapū to strengthen our collective voice and ensure you're included 
                in important community decisions, events, and cultural activities.
              </p>
              <button className="bg-ocean-teal text-white px-8 py-3 rounded-lg hover:bg-ocean-deep transition-colors font-semibold mr-4">
                Register with Hapū
              </button>
              <button className="border-2 border-ocean-teal text-ocean-teal px-8 py-3 rounded-lg hover:bg-ocean-teal hover:text-white transition-colors font-semibold">
                View Registry Guidelines
              </button>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="card text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Community Voice</h4>
                <p className="text-gray-600 text-sm">Participate in hapū decisions and governance</p>
              </div>
              <div className="card text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Cultural Connection</h4>
                <p className="text-gray-600 text-sm">Stay connected with hapū events and activities</p>
              </div>
              <div className="card text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Whakapapa Verification</h4>
                <p className="text-gray-600 text-sm">Support verification of genealogical connections</p>
              </div>
            </div>

            <div className="mt-8 card">
              <h4 className="font-semibold text-gray-800 mb-4">Registration Benefits</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2 text-gray-600">
                  <li>• Voting rights in hapū decisions</li>
                  <li>• Access to hapū resources and support</li>
                  <li>• Invitations to cultural events and hui</li>
                  <li>• Connection with other hapū members</li>
                </ul>
                <ul className="space-y-2 text-gray-600">
                  <li>• Whakapapa documentation support</li>
                  <li>• Cultural education opportunities</li>
                  <li>• Support for whenua connections</li>
                  <li>• Protection of collective interests</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Whakapapa Tool */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Digital Whakapapa Builder</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="card bg-ocean-blue bg-opacity-10 text-center">
              <TreePine className="h-16 w-16 text-ocean-teal mx-auto mb-6" />
              <h3 className="text-2xl font-semibold mb-4 text-ocean-deep">Interactive Family Tree</h3>
              <p className="text-gray-700 mb-6">
                Build and maintain your digital whakapapa with our interactive tool. 
                Connect with other whānau members and collaborate on your shared genealogy.
              </p>
              <button className="bg-ocean-teal text-white px-8 py-3 rounded-lg hover:bg-ocean-deep transition-colors font-semibold">
                Start Building Your Tree
              </button>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Collaborative</h4>
                <p className="text-gray-600 text-sm">Work together with whānau members</p>
              </div>
              <div className="text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Secure</h4>
                <p className="text-gray-600 text-sm">Respect cultural protocols and privacy</p>
              </div>
              <div className="text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Cultural</h4>
                <p className="text-gray-600 text-sm">Designed specifically for Māori genealogy</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-ocean-teal text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ko wai koe? Who are you?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Your whakapapa is your connection to the past, present, and future. 
            Start your journey of discovery today and strengthen your cultural identity.
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Discover</h3>
              <p className="text-ocean-blue text-opacity-80">Uncover your ancestral connections</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Connect</h3>
              <p className="text-ocean-blue text-opacity-80">Build relationships with whānau</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Belong</h3>
              <p className="text-ocean-blue text-opacity-80">Find your place in the iwi collective</p>
            </div>
          </div>        </div>
      </section>      {/* Modal Components */}
      <PersonForm
        person={editingPerson || undefined}
        people={familyData.people}
        isOpen={showPersonForm}
        onClose={() => setShowPersonForm(false)}
        onSave={handleSavePerson}
      />

      {selectedPerson && (
        <>
          <MediaManager
            personId={selectedPerson.id}
            personName={selectedPerson.name}
            photos={selectedPerson.photos || []}
            documents={selectedPerson.documents || []}
            isOpen={showMediaManager}
            onClose={() => setShowMediaManager(false)}
            onUpdate={(photos, documents) => handleMediaUpdate(selectedPerson.id, photos, documents)}
          />

          <RelationshipManager
            person={selectedPerson}
            people={familyData.people}
            isOpen={showRelationshipManager}
            onClose={() => setShowRelationshipManager(false)}
            onUpdate={handleRelationshipUpdate}
          />
        </>
      )}
    </div>
  );
};

export default Whakapapa;
